import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUpload, FiImage, FiX, FiCheck, FiLoader } from 'react-icons/fi';
import axios from 'axios';

const ImageUpload = ({
  endpoint = '/admin/upload/image',
  onUploadSuccess = () => {},
  onUploadError = () => {},
  accept = 'image/*',
  maxSize = 5, // MB
  multiple = false,
  category = 'general',
  prefix = '',
  className = '',
  label = 'Upload Image',
  currentImage = null,
  onImageRemove = () => {},
  showPreview = true,
  disabled = false
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    
    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File size should be less than ${maxSize}MB`);
      return;
    }
    
    setError('');
    
    // Create preview
    if (showPreview) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }
    
    // Upload file
    uploadFile(file);
  };

  const uploadFile = async (file) => {
    setUploading(true);
    setUploadProgress(0);
    setError('');
    
    const formData = new FormData();
    
    if (endpoint.includes('transformation-images')) {
      // For transformation images, determine if it's before or after
      if (prefix === 'before') {
        formData.append('before_image', file);
      } else {
        formData.append('after_image', file);
      }
    } else if (endpoint.includes('recipe-image')) {
      formData.append('recipe_image', file);
      formData.append('recipe_name', prefix);
    } else {
      formData.append('image', file);
      formData.append('category', category);
      formData.append('prefix', prefix);
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      const fullUrl = `${apiBaseUrl}${endpoint}`;
      
      console.log('📤 Image upload request:', {
        endpoint,
        apiBaseUrl,
        fullUrl,
        hasToken: !!token
      });
      
      const response = await axios.post(
        fullUrl,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(progress);
          }
        }
      );
      
      setUploadProgress(100);
      onUploadSuccess(response.data);
      
      // Auto-clear success state after 2 seconds
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 2000);
      
    } catch (error) {
      console.error('❌ Upload error:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
      });
      
      let errorMessage = 'Upload failed';
      if (error.response?.status === 405) {
        errorMessage = 'Method not allowed. Please check the endpoint URL.';
      } else if (error.response?.status === 413) {
        errorMessage = `File too large. Maximum size is ${maxSize}MB.`;
      } else {
        errorMessage = error.response?.data?.error || error.message || 'Upload failed';
      }
      
      setError(errorMessage);
      setUploading(false);
      setUploadProgress(0);
      onUploadError(errorMessage);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveImage = () => {
    setPreview(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onImageRemove();
  };

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileInput}
        className="hidden"
        disabled={disabled}
      />
      
      {/* Upload Area */}
      <motion.div
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center transition-all
          ${dragActive ? 'border-primary-400 bg-primary-50' : 'border-neutral-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary-400 hover:bg-neutral-50'}
          ${error ? 'border-danger-400 bg-danger-50' : ''}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFileDialog}
        whileHover={!disabled ? { scale: 1.02 } : {}}
        whileTap={!disabled ? { scale: 0.98 } : {}}
      >
        {/* Upload content */}
        <AnimatePresence mode="wait">
          {uploading ? (
            <motion.div
              key="uploading"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div className="flex justify-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <FiLoader className="w-8 h-8 text-primary-500" />
                </motion.div>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-700">Uploading...</p>
                <div className="mt-2 w-full bg-neutral-200 rounded-full h-2">
                  <motion.div
                    className="bg-primary-500 h-2 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${uploadProgress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <p className="text-xs text-neutral-500 mt-1">{uploadProgress}%</p>
              </div>
            </motion.div>
          ) : uploadProgress === 100 && !error ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-secondary-600"
            >
              <div className="flex justify-center mb-2">
                <FiCheck className="w-8 h-8" />
              </div>
              <p className="text-sm font-medium">Upload successful!</p>
            </motion.div>
          ) : (
            <motion.div
              key="default"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div className="flex justify-center mb-4">
                <FiUpload className="w-8 h-8 text-neutral-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-1">{label}</p>
                <p className="text-xs text-neutral-500">
                  Drag and drop or click to select
                </p>
                <p className="text-xs text-neutral-400 mt-1">
                  Supports: JPG, PNG, WebP (max {maxSize}MB)
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      
      {/* Image Preview */}
      {showPreview && preview && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 relative"
        >
          <div className="relative inline-block">
            <img
              src={preview}
              alt="Preview"
              className="w-full max-w-xs h-48 object-cover rounded-lg shadow-md"
            />
            {/* Remove button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveImage();
              }}
              className="absolute -top-2 -right-2 w-6 h-6 bg-danger-500 text-white rounded-full flex items-center justify-center hover:bg-danger-600 transition-colors"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
      
      {/* Error message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 p-3 bg-danger-50 border border-danger-200 rounded-md"
          >
            <p className="text-sm text-danger-700 font-medium">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImageUpload;
